import { createEntityAdapter } from '@reduxjs/toolkit';
import { createApi, fakeBaseQuery, type QueryReturnValue } from '@reduxjs/toolkit/query/react';
import { setPage, type ReportQueryState } from './reportsQuerySlice';
import type { ReportDetail } from './report';
import type {
  BackofficeReportProjection,
  BackofficeCanonicalReportFilterItem,
  ReportMapPoint,
  ReportPhotoAccessUrls,
  ReportsBackendGateway,
} from './reportsGateway';
import type { AppError } from '../../utils/firebaseErrors';
import type { AppDependencies } from '../../infrastructure/appDependencies';
import { isBackendGatewayError, toAppError } from '../../utils/backendGatewayError';
import { createReportReadInvalidationCoordinator } from './reportReadInvalidationCoordinator';
import { type StaleReadMeta } from '../staleReadFallback/staleReadCache';
import { buildReportsTableStaleReadKeys } from '../staleReadFallback/queryKeys';
import { DEFAULT_REPORT_SORT, readReportListFamily } from './reportListReadFamily';
import { readReportDetailFamily } from './reportDetailReadFamily';
import { readReportMapFamily } from './reportMapReadFamily';
import { toReportInvalidQueryError } from './reportQueryErrors';
import {
  doesReportMatchListFilters,
  getLastValidReportListPage,
  isReportPresenceLost,
  reconcileReportPresenceLostListPage,
  recordReportPresenceLost,
} from './reportReadFamilyPolicy';
import { reportPresenceLost } from './reportReviewWorkflow/reportReviewWorkflowSlice';
export { toCanonicalReportFilters } from './reportQueryCapabilities';

export interface ReportsFilterItem {
  field: string;
  op: string;
  value: string | string[];
}
export type ReportsFilter = ReportsFilterItem[];

export interface PaginatedReportsFrontend {
  items: BackofficeReportProjection[];
  page: number;
  pageSize: number;
  totalCount: number;
  staleRead?: StaleReadMeta;
}

export type PaginatedReports = PaginatedReportsFrontend;

type StaleReadable<T> = T & { staleRead?: StaleReadMeta };
type ReportMapResponse = StaleReadable<ReportMapPoint[]>;
type ReportDetailResponse = ReportDetail | undefined;

export const REPORT_TIMEZONE_DEFAULT = 'Europe/Rome';

const reportsAdapter = createEntityAdapter<BackofficeReportProjection>();

export const {
  selectAll: selectAllReports,
  selectById: selectReportById,
  selectIds: selectReportIds,
} = reportsAdapter.getSelectors();

export type GetReportsArgs = {
  filters: ReportsFilterItem[];
  page: number;
  pageSize: number;
  sort?: {
    field: string;
    direction: string;
  };
};

export type GetReportsForMapArgs = {
  filters: ReportsFilterItem[];
};

const getAppDependencies = (extra: unknown): AppDependencies => {
  if (extra && typeof extra === 'object' && 'reportsBackend' in extra) {
    return extra as AppDependencies;
  }

  throw new Error('App Runtime dependencies are not configured for reports API');
};

const getReportsBackend = (extra: unknown): ReportsBackendGateway =>
  getAppDependencies(extra).reportsBackend;

export const getReportBrowserTimezone = (): string => REPORT_TIMEZONE_DEFAULT;

const toInvalidQueryError = (error: unknown): AppError => toReportInvalidQueryError(error);

const isInvalidQueryFailure = (error: unknown): boolean => {
  if (isBackendGatewayError(error)) {
    if (error.reason.includes('invalid-query')) {
      return true;
    }

    return isInvalidQueryFailure(error.original);
  }

  if (!error || typeof error !== 'object') {
    return false;
  }

  const record = error as { code?: unknown; reason?: unknown };
  const code = typeof record.code === 'string' ? record.code : '';
  const reason = typeof record.reason === 'string' ? record.reason : '';

  return (
    code.includes('invalid-argument') ||
    code.includes('invalid-query') ||
    reason.includes('invalid-query')
  );
};

export const reportsApi = createApi({
  reducerPath: 'reportsApi',
  baseQuery: fakeBaseQuery<AppError>(),
  tagTypes: [
    'Reports',
    'ReportsList',
    'ReportsDetail',
    'ReportsMap',
    'ReportsHeatmap',
    'ReportPhotoAccess',
  ],
  keepUnusedDataFor: 300,
  refetchOnMountOrArgChange: 30,
  endpoints: (builder) => ({
    getReportsForTable: builder.query<PaginatedReportsFrontend, GetReportsArgs>({
      async queryFn(
        { filters = [], page, pageSize, sort },
        { dispatch, extra }
      ): Promise<QueryReturnValue<PaginatedReportsFrontend, AppError, {}>> {
        const reportsBackend = getReportsBackend(extra);
        return readReportListFamily({
          filters,
          page,
          pageSize,
          sort,
          onPageCorrection: (nextPage) => {
            dispatch(setPage(nextPage));
          },
          read: (params) =>
            reportsBackend.getReportsForTable({
              filters: params.filters,
              page: params.page,
              pageSize: params.pageSize,
              sort: params.sort,
            }),
        });
      },
      providesTags: ['Reports', 'ReportsList'],
    }),
    getReportsForMap: builder.query<ReportMapResponse, GetReportsForMapArgs>({
      async queryFn(
        { filters = [] },
        { extra }
      ): Promise<QueryReturnValue<ReportMapResponse, AppError, {}>> {
        const reportsBackend = getReportsBackend(extra);
        return readReportMapFamily({
          filters,
          isInvalidQueryFailure,
          toInvalidQueryError,
          toAppError,
          read: (canonicalFilters: BackofficeCanonicalReportFilterItem[]) =>
            reportsBackend.getReportsForMap({ filters: canonicalFilters }),
        });
      },
      providesTags: ['Reports', 'ReportsMap', 'ReportsHeatmap'],
    }),
    getReportDetailById: builder.query<ReportDetailResponse, string>({
      queryFn: async (
        id,
        { extra }
      ): Promise<QueryReturnValue<ReportDetailResponse, AppError, {}>> => {
        const reportsBackend = getReportsBackend(extra);
        return readReportDetailFamily({
          reportId: id,
          read: () => reportsBackend.getReportDetailById(id),
          isPresenceLost: isReportPresenceLost,
          isInvalidQueryFailure,
          toInvalidQueryError,
          toAppError,
        });
      },
      providesTags: (_result, _error, id) => ['Reports', 'ReportsDetail', { type: 'Reports', id }],
    }),
    getReportPhotoAccessUrls: builder.query<ReportPhotoAccessUrls, string>({
      queryFn: async (
        id,
        { extra }
      ): Promise<QueryReturnValue<ReportPhotoAccessUrls, AppError, {}>> => {
        if (isReportPresenceLost(id)) {
          return { data: { reportId: id, items: [] } };
        }

        const reportsBackend = getReportsBackend(extra);
        try {
          const urls = await reportsBackend.getReportPhotoAccessUrls(id);
          if (isReportPresenceLost(id)) {
            return { data: { reportId: id, items: [] } };
          }
          return { data: urls };
        } catch (error: unknown) {
          return { error: toAppError(error) };
        }
      },
      keepUnusedDataFor: 60,
      providesTags: (_result, _error, id) => [{ type: 'ReportPhotoAccess', id }],
    }),
    subscribeToReports: builder.query<PaginatedReportsFrontend, { listenerId: string }>({
      async queryFn(): Promise<QueryReturnValue<PaginatedReportsFrontend, AppError, {}>> {
        return { data: { items: [], page: 0, pageSize: 0, totalCount: 0 } };
      },
      keepUnusedDataFor: 0,
      async onCacheEntryAdded(
        arg,
        { cacheDataLoaded, cacheEntryRemoved, dispatch, extra, getState }
      ) {
        const reportsInvalidation = getAppDependencies(extra).reportsInvalidation;
        const coordinateReadInvalidation = createReportReadInvalidationCoordinator(
          (tags) => {
            dispatch(reportsApi.util.invalidateTags(tags));
          },
          {
            onReportPresenceLost: ({ reportId, reason }) => {
              const wasPresenceAlreadyLost = isReportPresenceLost(reportId);
              const stateBeforePresenceLoss = getState();
              const cachedTableArgs = reportsApi.util.selectCachedArgsForQuery(
                stateBeforePresenceLoss,
                'getReportsForTable'
              );
              const cachedTableEntries = cachedTableArgs.map((queryArgs) => ({
                queryArgs,
                data: reportsApi.endpoints.getReportsForTable.select(queryArgs)(
                  stateBeforePresenceLoss
                ).data,
                filterIdentity: buildReportsTableStaleReadKeys({
                  filters: queryArgs.filters,
                  page: 0,
                  pageSize: 1,
                  sort: DEFAULT_REPORT_SORT,
                }).familyKey,
              }));
              const removedReport = cachedTableEntries
                .map(({ data }) => data?.items.find((report) => report.id === reportId))
                .find((report): report is BackofficeReportProjection => report !== undefined);
              const targetTotalCountByFilter = new Map<string, number>();

              cachedTableEntries.forEach(({ data, filterIdentity }) => {
                if (!data?.items.some((report) => report.id === reportId)) {
                  return;
                }

                const targetTotalCount = Math.max(0, data.totalCount - 1);
                const currentTarget = targetTotalCountByFilter.get(filterIdentity);
                targetTotalCountByFilter.set(
                  filterIdentity,
                  currentTarget === undefined
                    ? targetTotalCount
                    : Math.min(currentTarget, targetTotalCount)
                );
              });

              recordReportPresenceLost(reportId, reason);
              dispatch(reportPresenceLost({ reportId, reason }));

              cachedTableEntries.forEach(({ queryArgs, data, filterIdentity }) => {
                const isRelevantCache =
                  queryArgs.filters.length === 0 ||
                  (removedReport !== undefined &&
                    doesReportMatchListFilters(removedReport, queryArgs.filters));
                const didContainReport =
                  data?.items.some((report) => report.id === reportId) ?? false;
                const shouldReconcilePresenceLoss =
                  didContainReport || (!wasPresenceAlreadyLost && isRelevantCache);
                let visibleTotalCount = data?.totalCount ?? 0;
                dispatch(
                  reportsApi.util.updateQueryData('getReportsForTable', queryArgs, (draft) => {
                    const knownTargetTotalCount = targetTotalCountByFilter.get(filterIdentity);
                    const targetTotalCount = shouldReconcilePresenceLoss
                      ? (knownTargetTotalCount ?? Math.max(0, draft.totalCount - 1))
                      : undefined;
                    const visiblePage = reconcileReportPresenceLostListPage(
                      draft,
                      reportId,
                      targetTotalCount
                    );
                    visibleTotalCount = visiblePage.totalCount;
                    return visiblePage;
                  })
                );

                const activeQuery = (getState() as unknown as { reportQuery: ReportQueryState })
                  .reportQuery;
                const isActiveQuery =
                  buildReportsTableStaleReadKeys({
                    ...queryArgs,
                    sort: queryArgs.sort ?? DEFAULT_REPORT_SORT,
                  }).exactKey === buildReportsTableStaleReadKeys(activeQuery).exactKey;
                const lastValidPage = getLastValidReportListPage(
                  visibleTotalCount,
                  queryArgs.pageSize
                );
                if (
                  shouldReconcilePresenceLoss &&
                  isActiveQuery &&
                  queryArgs.page > lastValidPage
                ) {
                  dispatch(setPage(lastValidPage));
                }
              });
              reportsApi.util
                .selectCachedArgsForQuery(getState(), 'getReportsForMap')
                .forEach((queryArgs) => {
                  dispatch(
                    reportsApi.util.updateQueryData('getReportsForMap', queryArgs, (draft) =>
                      draft.filter((point) => point.id !== reportId)
                    )
                  );
                });
              if (
                reportsApi.util
                  .selectCachedArgsForQuery(getState(), 'getReportDetailById')
                  .includes(reportId)
              ) {
                dispatch(
                  reportsApi.util.patchQueryData('getReportDetailById', reportId, [
                    { op: 'replace', path: [], value: undefined },
                  ])
                );
              }
              dispatch(
                reportsApi.util.updateQueryData('getReportPhotoAccessUrls', reportId, (draft) => {
                  draft.reportId = reportId;
                  draft.items = [];
                })
              );
              dispatch(
                reportsApi.util.invalidateTags([
                  { type: 'Reports', id: reportId },
                  { type: 'ReportPhotoAccess', id: reportId },
                ])
              );
            },
          }
        );

        const unsubscribe = reportsInvalidation.setupReportsListener(
          arg.listenerId,
          coordinateReadInvalidation
        );

        await cacheDataLoaded;

        await cacheEntryRemoved;
        coordinateReadInvalidation.cancel();
        unsubscribe();
      },
      providesTags: ['Reports'],
    }),
    updateReport: builder.mutation<void, { reportId: string; species: string }>({
      queryFn: async (): Promise<QueryReturnValue<void, AppError, {}>> => {
        return {
          error: {
            code: 'report/not-implemented',
            message:
              'Update species functionality not yet implemented with cloud functions. Please use direct Firestore operations for species updates.',
          },
        };
      },
      invalidatesTags: ['Reports', 'ReportsList', 'ReportsDetail', 'ReportsMap', 'ReportsHeatmap'],
    }),
  }),
});

export const {
  useGetReportsForTableQuery,
  useGetReportsForMapQuery,
  useSubscribeToReportsQuery,
  useGetReportDetailByIdQuery,
  useGetReportPhotoAccessUrlsQuery,
  useUpdateReportMutation,
  util: { invalidateTags },
} = reportsApi;
